import { Mail, UserPlus } from 'lucide-react'
import { useState, type FormEvent, type ReactNode } from 'react'
import { FormError } from '@/components/FormError'
import { Avatar } from '@/components/tree/Avatar'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  bothKnow,
  useAnswer,
  useAsk,
  useConnections,
  useTakeBack,
  type Account,
  type ConnectionRequest,
  type Suggestion,
  type WaitingRequest,
} from '@/lib/connections'

const NOTE = 280
// People you may know shows this many until asked for the rest.
const FIRST = 5

// Connecting one to one, on People: who's asking to connect with you (open their profile to see who they are before
// you say yes), people you may know (in a circle with someone you know), and the requests you've
// made that are still waiting. Asking someone new (your QR code, by email, or Search) is the "+" at the top
// (AddConnection.tsx).
export function Connect() {
  const connections = useConnections()
  const [request, setRequest] = useState<ConnectionRequest | null>(null)
  const [asking, setAsking] = useState<Suggestion | null>(null)
  const [all, setAll] = useState(false)
  const data = connections.data
  if (!data) return <FormError error={connections.error} />
  const suggestions = all ? data.suggestions : data.suggestions.slice(0, FIRST)
  return (
    <div className="flex flex-col gap-8">
      {data.requests.length > 0 && (
        <Section id="asking" title="Wants to connect">
          {data.requests.map((x) => (
            <Row
              key={x.id}
              person={x.from}
              line={
                x.note ? `“${x.note}”` : x.mutual.length ? `You both know ${bothKnow(x.mutual)}` : 'Asked to connect'
              }
              onOpen={() => setRequest(x)}
              action={
                <Button size="sm" onClick={() => setRequest(x)} aria-label={`See ${x.from.name}’s profile`}>
                  See profile
                </Button>
              }
            />
          ))}
        </Section>
      )}

      {data.suggestions.length > 0 && (
        <Section id="may-know" title="People you may know">
          {suggestions.map((x) => (
            <Row
              key={x.id}
              person={x}
              line={x.mutual.length ? `Knows ${bothKnow(x.mutual)}` : ''}
              onOpen={() => setAsking(x)}
              action={
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => setAsking(x)}
                  aria-label={`Connect with ${x.name}`}
                >
                  Connect
                </Button>
              }
            />
          ))}
          {!all && data.suggestions.length > FIRST && (
            <Button variant="ghost" size="sm" className="self-start" onClick={() => setAll(true)}>
              Show {data.suggestions.length - FIRST} more
            </Button>
          )}
        </Section>
      )}

      {data.waiting.length > 0 && <Waiting waiting={data.waiting} />}

      <RequestProfile request={request} onClose={() => setRequest(null)} />
      <AskDialog person={asking} onClose={() => setAsking(null)} />
    </div>
  )
}

function Section({ id, title, children }: { id: string; title: string; children: ReactNode }) {
  return (
    <section aria-labelledby={id} className="flex flex-col gap-1">
      <h2 id={id} className="text-sm font-semibold">
        {title}
      </h2>
      <ul className="-mx-2 flex flex-col">{children}</ul>
    </section>
  )
}

export function Row({
  person,
  line,
  onOpen,
  action,
}: {
  person: Account
  line: string
  onOpen: () => void
  action: ReactNode
}) {
  return (
    <li className="flex min-h-14 items-center gap-3 rounded-xl px-2 py-1.5 hover:bg-secondary">
      <button
        type="button"
        onClick={onOpen}
        className="flex min-w-0 flex-1 items-center gap-3 rounded-lg text-left outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
      >
        <Avatar person={person} className="size-11 text-base" />
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="truncate text-[0.9375rem] leading-snug font-semibold">{person.name}</span>
          {line && <span className="truncate text-sub text-muted-foreground">{line}</span>}
        </span>
      </button>
      {action}
    </li>
  )
}

const since = (day: string) => {
  const date = new Date(`${day}T12:00:00Z`)
  return Number.isNaN(date.getTime())
    ? ''
    : date.toLocaleDateString('en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' })
}

// Who's asking, before you answer: their face and name, what they wrote, who you both know and when they joined.
function RequestProfile({ request, onClose }: { request: ConnectionRequest | null; onClose: () => void }) {
  const answer = useAnswer()
  const [shown, setShown] = useState<ConnectionRequest | null>(null)
  if (request && request !== shown) {
    setShown(request)
    answer.reset()
  }
  const x = request ?? shown
  return (
    <Dialog open={!!request} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90svh] overflow-y-auto sm:max-w-sm">
        {x && (
          <div className="flex flex-col items-center gap-5 text-center">
            <Avatar person={x.from} variant="display" className="size-28 text-5xl" />
            <DialogHeader className="items-center text-center sm:text-center">
              <DialogTitle className="text-title text-balance">{x.from.name}</DialogTitle>
              <DialogDescription>
                {x.from.name} {x.link ? 'opened your link and would' : 'would'} like to connect with you
                {since(x.joined) ? ` · on gatheredfrom since ${since(x.joined)}` : ''}
              </DialogDescription>
            </DialogHeader>
            {x.note && <blockquote className="text-body text-balance italic">“{x.note}”</blockquote>}
            <p className="text-sub text-muted-foreground">
              {x.mutual.length ? `You both know ${bothKnow(x.mutual)}.` : 'You don’t know anyone in common here yet.'}
            </p>
            <p className="text-sub text-muted-foreground">
              Once you’re connected, you each see what the other shares with Everyone. You can end it any time.
            </p>
            <FormError error={answer.error} />
            <div className="flex w-full flex-col gap-2">
              <Button
                disabled={answer.isPending}
                onClick={() => answer.mutate({ id: x.id, yes: true }, { onSuccess: onClose })}
              >
                Accept
              </Button>
              <Button
                variant="ghost"
                disabled={answer.isPending}
                onClick={() => answer.mutate({ id: x.id, yes: false }, { onSuccess: onClose })}
              >
                Not now
              </Button>
              <p className="text-xs text-muted-foreground">They aren’t told if you choose Not now.</p>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

function NoteField({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div className="grid gap-2 text-left">
      <Label htmlFor="connect-note">A note (optional)</Label>
      <Textarea
        id="connect-note"
        maxLength={NOTE}
        rows={3}
        placeholder="Say who you are"
        value={value}
        // One line: notes are short, and line breaks aren't kept.
        onChange={(e) => onChange(e.target.value.replace(/[\r\n]+/g, ' '))}
      />
    </div>
  )
}

// Asking someone you may know.
export function AskDialog({ person, onClose }: { person: Suggestion | null; onClose: () => void }) {
  const ask = useAsk()
  const [note, setNote] = useState('')
  const [shown, setShown] = useState<Suggestion | null>(null)
  if (person && person !== shown) {
    setShown(person)
    setNote('')
    ask.reset()
  }
  const x = person ?? shown
  function submit(e: FormEvent) {
    e.preventDefault()
    if (!x) return
    ask.mutate({ to: x.id, ...(note.trim() ? { note: note.trim() } : {}) }, { onSuccess: onClose })
  }
  return (
    <Dialog open={!!person} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90svh] overflow-y-auto sm:max-w-sm">
        {x && (
          <form onSubmit={submit} className="flex flex-col items-center gap-5 text-center">
            <Avatar person={x} variant="display" className="size-28 text-5xl" />
            <DialogHeader className="items-center text-center sm:text-center">
              <DialogTitle className="text-title text-balance">{x.name}</DialogTitle>
              <DialogDescription>
                {x.mutual.length ? `Knows ${bothKnow(x.mutual)}` : 'In a circle with someone you know'}
              </DialogDescription>
            </DialogHeader>
            <div className="w-full">
              <NoteField value={note} onChange={setNote} />
            </div>
            <FormError error={ask.error} />
            <Button type="submit" className="w-full" disabled={ask.isPending}>
              <UserPlus aria-hidden /> Ask to connect
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  )
}

// Asking by email. The answer is the same whether or not the address has an account, so this never tells anyone who's
// on gatheredfrom.
export function EmailDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const ask = useAsk()
  const [email, setEmail] = useState('')
  const [note, setNote] = useState('')
  const [said, setSaid] = useState<string | null>(null)
  function change(next: boolean) {
    if (next) return
    onClose()
    setEmail('')
    setNote('')
    setSaid(null)
    ask.reset()
  }
  function submit(e: FormEvent) {
    e.preventDefault()
    ask.mutate(
      { email: email.trim(), ...(note.trim() ? { note: note.trim() } : {}) },
      {
        onSuccess: (r) =>
          setSaid(
            r.already
              ? 'You’re already connected.'
              : r.connected
                ? 'They’d already asked you, so you’re connected now.'
                : `Asked. If ${email.trim()} is on gatheredfrom, they’ll see your request on People.`,
          ),
      },
    )
  }
  return (
    <Dialog open={open} onOpenChange={change}>
      <DialogContent className="max-h-[90svh] overflow-y-auto sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Connect by email</DialogTitle>
          <DialogDescription>
            Ask someone you know by their email address. If they’re on gatheredfrom, they’ll see your request on People.
            We never say whether an address has an account.
          </DialogDescription>
        </DialogHeader>
        {said ? (
          <div className="flex flex-col gap-4">
            <p role="status">{said}</p>
            <Button className="self-start" onClick={() => change(false)}>
              Done
            </Button>
          </div>
        ) : (
          <form onSubmit={submit} className="grid gap-4">
            <div className="grid gap-2">
              <Label htmlFor="connect-email">Their email</Label>
              <Input
                id="connect-email"
                type="email"
                required
                maxLength={254}
                autoComplete="off"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <NoteField value={note} onChange={setNote} />
            <FormError error={ask.error} />
            <Button type="submit" className="self-start" disabled={ask.isPending}>
              Ask to connect
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  )
}

function Waiting({ waiting }: { waiting: WaitingRequest[] }) {
  const takeBack = useTakeBack()
  return (
    <section aria-labelledby="asked" className="flex flex-col gap-1">
      <h2 id="asked" className="text-sm font-semibold">
        You asked
      </h2>
      <FormError error={takeBack.error} />
      <ul className="-mx-2 flex flex-col">
        {waiting.map((x) => {
          const who = 'email' in x.to ? x.to.email : x.to.name
          return (
            <li key={x.id} className="flex min-h-14 items-center gap-3 rounded-xl px-2 py-1.5">
              {'email' in x.to ? (
                <span className="grid size-11 shrink-0 place-items-center rounded-full bg-secondary text-muted-foreground">
                  <Mail className="size-5" aria-hidden />
                </span>
              ) : (
                <Avatar person={x.to} className="size-11 text-base" />
              )}
              <span className="flex min-w-0 flex-1 flex-col">
                <span className="truncate text-[0.9375rem] leading-snug font-semibold">{who}</span>
                <span className="text-sub text-muted-foreground">Waiting</span>
              </span>
              <Button
                size="sm"
                variant="ghost"
                disabled={takeBack.isPending}
                onClick={() => takeBack.mutate(x.id)}
                aria-label={`Take back your request to ${who}`}
              >
                Take back
              </Button>
            </li>
          )
        })}
      </ul>
    </section>
  )
}
